import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { LayoutGrid, X, Home } from 'lucide-react';
import { visibleCategories, entriesInCategory, categoryCount, entryPath } from '../../utils/guideIndex';
import { ExplorerNavLinks } from './ExplorerNavLinks';
import { VerificationAccessLink } from './VerificationAccessLink';
const EASE = [0.23, 1, 0.32, 1] as const;
export function GuideMobileMenu() {
  const [open, setOpen] = useState(false);
  const {
    pathname
  } = useLocation();
  const reduceMotion = useReducedMotion();
  const activeCategory = pathname.split('/')[2];
  const d = (s: number) => reduceMotion ? 0 : s;
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  return createPortal(<div className="lg:hidden" data-id="element-647" data-mp="1jiqvipr4lf80">
      <motion.button type="button" onClick={() => setOpen(true)} aria-haspopup="dialog" initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: open ? 0 : 1,
      y: 0
    }} transition={{
      duration: d(0.22),
      ease: EASE
    }} whileTap={{
      scale: 0.95
    }} style={{
      pointerEvents: open ? 'none' : 'auto'
    }} className="fixed bottom-5 left-4 z-50 flex h-12 items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-[#0A1628]/90 px-5 font-cinzel text-sm font-bold text-[#D4AF37] shadow-[0_8px_30px_rgba(0,0,0,0.5)] backdrop-blur-xl" data-id="element-648" data-mp="1jlvbekdydzei">
        <LayoutGrid className="h-4 w-4" aria-hidden="true" data-id="element-649" data-mp="2c63d0lifiwug" />
        Categorías
      </motion.button>

      <AnimatePresence data-id="element-650" data-mp="2h4wpcalzcht1">
        {open && <>
            <motion.div key="backdrop" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: d(0.2)
        }} className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} aria-hidden="true" data-id="element-651" data-mp="1rjwywsch03az" />
            <motion.div key="sheet" role="dialog" aria-modal="true" aria-label="Categorías de la guía" initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 24
        }} transition={{
          duration: d(0.26),
          ease: EASE
        }} className="fixed inset-x-0 bottom-0 z-[71] max-h-[82vh] overflow-y-auto rounded-t-3xl border-t border-[#D4AF37]/30 bg-[#0A1628] px-4 pb-8 pt-4" data-id="element-652" data-mp="2a8ttyyfoyzu9">
              <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-white/20" aria-hidden="true" data-id="element-653" data-mp="kw9lvg8wwix1" />
              <div className="mb-2 flex items-center justify-between px-2" data-id="element-654" data-mp="2wik386l6pge3">
                <p className="font-cinzel text-base font-bold text-white" data-id="element-655" data-mp="31g3gb365bn4g">Guía</p>
                <button type="button" onClick={() => setOpen(false)} className="flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-gray-400 hover:text-white" aria-label="Cerrar" data-id="element-656" data-mp="2msqtzc8ildd0">
                  <X className="h-4 w-4" data-id="element-657" data-mp="2dr11s46t6ae6" />
                </button>
              </div>
              <Link to="/guia" className="flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold text-gray-200 hover:bg-white/5" data-id="element-658" data-mp="2hsw3h9snipsh">
                <Home className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-659" data-mp="23uo7p57cpuiw" />
                Inicio de la guía
              </Link>
              <p className="mt-3 mb-1 px-3 text-xs font-semibold text-gray-500" data-id="element-660" data-mp="28uannmekdove">Explorador</p>
              <ExplorerNavLinks size="lg" data-id="element-661" data-mp="1zx2ddga5ni1" />
              <p className="mt-3 mb-1 px-3 text-xs font-semibold text-gray-500" data-id="element-662" data-mp="71cpdb20q432">Categorías</p>
              <ul className="space-y-0.5" data-id="element-663" data-mp="234r589uiyjgo">
                {visibleCategories.map(c => {
              const Icon = c.icon;
              const isActive = activeCategory === c.id;
              const entries = entriesInCategory(c.id);
              return <li key={c.id} data-id="element-664" data-mp="1p4gdl4le50ug">
                      <Link to={`/guia/${c.id}`} className={`flex items-center gap-3 rounded-xl px-3 py-3 text-sm transition-colors duration-150 ${isActive ? 'bg-[#D4AF37]/15 text-[#D4AF37]' : 'text-gray-200 hover:bg-white/5'}`} data-id="element-665" data-mp="1p3erm52u6txy">
                        <Icon className="h-4 w-4 shrink-0 text-[#D4AF37]" aria-hidden="true" data-id="element-666" data-mp="1agljpkvncqfs" />
                        <span className="flex-1" data-id="element-667" data-mp="16f01dk0cfjy7">{c.title}</span>
                        <span className="text-xs tabular-nums text-gray-500" data-id="element-668" data-mp="11evsaf1rlri0">{categoryCount(c.id) || 'Pronto'}</span>
                      </Link>
                      {isActive && entries.length > 0 && <ul className="mb-2 ml-7 border-l border-white/10" data-id="element-669" data-mp="383eaprez62ld">
                          {entries.map(e => <li key={e.id} data-id="element-670" data-mp="3d163sorcvur5">
                              <Link to={entryPath(e)} className={`block py-2.5 pl-4 text-sm ${pathname === entryPath(e) ? 'text-[#D4AF37]' : 'text-gray-400'}`} data-id="element-671" data-mp="3d4bprv4ougin">
                                {e.title}
                              </Link>
                            </li>)}
                        </ul>}
                    </li>;
            })}
              </ul>
              <div className="mt-5" data-id="element-672" data-mp="2ydkxyn33jhfj">
                <VerificationAccessLink size="lg" data-id="element-673" data-mp="2ycjepvbxfomq" />
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>, document.body);
}